import React, {
  useRef,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import classes from "./ClientsScreen.module.css";
import SearchBar from "../components/common/SearchBar";
import ClientsList, { ClientsListRef } from "../components/clients/ClientsList";
import ClientsListLetterNavigation from "../components/clients/ClientsListLetterNavigation";
import InviteClientModal from "../components/clients/InviteClientModal";

import PageLoader from "../../../common/ui/PageLoader";
import SecondaryTabMenuPicker from "../components/common/SecondaryTabMenuPicker";
import PrimaryButton from "../../../common/ui/PrimaryButton";
import MessageIcon from "../svg/MessageIcon";
import { ImportClient } from "../../../common/network/Api";
import { useAuth } from "../../../providers/AuthProvider";
import { ClientType } from "../../../common/entities/profile";
import FilterSelect from "../components/prescriptionTab/FilterSelect";

export const ClientListTabs = [
  { label: "All clients", key: 0 },
  { label: "Registered", key: 1 },
  { label: "Non-Registered", key: 2 },
];

const ClientsScreen = () => {
  const [clientFilter, setClientFilter] = useState("");
  const [isLoadingCustomerInfo, setIsLoadingCustomerInfo] = useState(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedScores, setSelectedScores] = useState<string[]>([]);
  const [clientsData, setClientsData] = useState<ClientType[]>([]);
  const { AuthUser } = useAuth();

  const clientsListRef = useRef<ClientsListRef>(null);

  const handleClientInvited = async () => {
    if (clientsListRef.current) {
      await clientsListRef.current.fetchClients();
    }
  };

  // Get unique origins for FilterPicker - calculated when clients are loaded
  const uniqueOrigins = useMemo(() => {
    const origins = new Set<string>();
    clientsData.forEach((client: ClientType) => {
      if (client.origin === "") {
        origins.add("PNOE");
      } else {
        if (client.isImport) {
          origins.add(client.origin);
        }
      }
    });
    return Array.from(origins);
  }, [clientsData]);

  const originOptions = useMemo(() => {
    return uniqueOrigins.map((origin) => ({
      key: origin,
      label: origin === "PNOE" ? "PNOE" : origin,
      origin: origin === "PNOE" ? "" : origin,
    }));
  }, [uniqueOrigins]);

  const scoreOptions = [
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "partially_approved", label: "Partially Approved" },
    { key: "hidden", label: "Hidden" },
  ];

  const handleClientsUpdated = useCallback((clients: ClientType[]) => {
    setClientsData(clients);
  }, []);
  if (isLoadingCustomerInfo) {
    return (
      <div className={classes.container}>
        <PageLoader />
      </div>
    );
  }

  const handleImportClient = async () => {
    try {
      const mockData = {
        id: AuthUser.pnoe_user_id,
        firstName: "Theo",
        lastName: "Gilos",
        email: "theodoros.gilos+866@pnoe.com",
        birthDate: "1990-06-15",
        gender: 1,
        height: 110,
        heightType: 1,
        weight: 80,
        weightType: 1,
        exerciseGoal: 3,
        exerciseFrequency: 2,
        secondaryExerciseGoal: 2,
        lifeStyle: 2,
        occupation: 3,
        physicalActivityAtLeisure: 2,
        targetWeight: 80,
        speedToGoal: 1,
        fatMassGoal: 1,
        fatMassTarget: 1,
        muscleMassTarget: 1,
        muscleMassGoal: 1,
        fatLossIntensity: 1,
        resistance_equipment: 2,
        resistance_training_level: 0,
        cardio_training_level: 2,
        interval_training_level: 2,
        resistance_hypertrophy_focus_area: 0,
        resistance_strength_focus_area: 0,
        origin: "Keragon",
        import: 1,
      };

      await ImportClient(mockData);
    } catch (e: any) {
      console.log("Invite client error", e);
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.headerSection}>
        <SearchBar
          setSearchFilter={setClientFilter}
          searchFilter={clientFilter}
          label="Choose customer"
          placeholder="Choose client"
        />
        <PrimaryButton
          label={"Invite Client"}
          onClick={() => setIsInviteModalOpen(true)}
          icon={<MessageIcon />}
          sx={{ width: "185px" }}
        />
        {/*<PrimaryButton*/}
        {/*  label={"Import Client"}*/}
        {/*  onClick={handleImportClient}*/}
        {/*  sx={{ width: "175px", backgroundColor: "orange" }}*/}
        {/*/>*/}
      </div>
      <div className={classes.tabSection}>
        <SecondaryTabMenuPicker
          tabs={ClientListTabs}
          selectedTab={activeTab}
          setSelectedTab={setActiveTab}
          isFullWidth={false}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <FilterSelect
            title="Score"
            options={scoreOptions}
            selectedKeys={selectedScores}
            onSelectionChange={setSelectedScores}
          />
          <FilterSelect
            title="Source"
            options={originOptions}
            selectedKeys={selectedSources}
            onSelectionChange={setSelectedSources}
            showOriginLogic={true}
          />
        </div>
      </div>

      <div className={classes.mainContent}>
        <ClientsList
          ref={clientsListRef}
          clientFilter={clientFilter}
          setIsLoadingCustomerInfo={setIsLoadingCustomerInfo}
          activeTab={activeTab}
          selectedSources={selectedSources}
          selectedScores={selectedScores}
          onClientsUpdated={handleClientsUpdated}
        />
        <ClientsListLetterNavigation
          onSelectLetter={(letter) => clientsListRef.current?.scrollToLetter(letter)}
        />
      </div>
      <InviteClientModal
        open={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onClientInvited={handleClientInvited}
      />
    </div>
  );
};

export default ClientsScreen;
